import * as React from 'react';
import * as A from 'fp-ts/Array';
import * as O from 'fp-ts/Option';
import { Link } from 'react-router-dom';
import startCase from 'lodash.startcase';

import { pipe } from 'fp-ts/lib/function';
import {
  FullMatchFragment,
  useDeleteMatchEventMutation,
} from '../../../generated/graphql';

import { LoadingContainer } from '../../../components/LoadingContainer';
import { Heading } from '../../../components/Heading';
import { useAnalytics } from '../../../contexts/AnalyticsContext';
import { useAlert } from '../../../contexts/AlertContext';
import { ButtonLink, Button } from '../../../components/Button';
import { FeatherIcon } from '../../../components/FeatherIcon';
import { CardIcon } from '../components/CardIcon';

import { paths } from '../../../paths';
import { PickCardRecipient } from './PickCardRecipient';
import { CardMatchEventFragment } from './utils';
import { useMatchStats } from './useMatchStats';

interface Props {
  match: FullMatchFragment;
}

export const MatchCards: React.FC<Props> = ({ match }) => {
  const analytics = useAnalytics();
  const alert = useAlert();
  const [isSelectingCardRecipient, setSelectingCardRecipient] =
    React.useState(false);

  const { stats, fetching } = useMatchStats(match.id);

  const [, deleteCardRecipient] = useDeleteMatchEventMutation();

  if (isSelectingCardRecipient) {
    return (
      <PickCardRecipient
        match={match}
        onCancel={() => setSelectingCardRecipient(false)}
      />
    );
  }

  if (match.team.isCaptain && A.isEmpty(match.players)) {
    return (
      <p>
        <Link
          to={paths.match.settings.squad(match.id)}
          className="text-primary"
        >
          Set a team for this match
        </Link>{' '}
        to add card recipients.
      </p>
    );
  }

  const removeCardRecipient = (cardRecipientId: string) => {
    deleteCardRecipient({ id: cardRecipientId }).then((response) => {
      if (response && response.data && response.data.deleteMatchEvent) {
        const formEventMeta = { form: 'delete_card_recipient' };
        if (response.data.deleteMatchEvent.error) {
          analytics.track('Data Submit Failed', formEventMeta);
          alert('Something went wrong', 'danger');
          return;
        }
        if (response.data.deleteMatchEvent.match) {
          analytics.track('Data Submit Succeeded', formEventMeta);

          alert('Carded player deleted');
        }
      }
    });
  };

  return (
    <LoadingContainer loading={fetching}>
      <div className="space-y-4">
        <Heading>Match Cards</Heading>
        {stats.cards ? (
          <CardItemList
            cardRecipients={stats.cards}
            removeCardRecipient={removeCardRecipient}
          />
        ) : (
          <p>No carded players</p>
        )}
        <Button onClick={() => setSelectingCardRecipient(true)} icon="plus">
          Add carded player
        </Button>
        <ButtonLink
          to={paths.match.home(match.id)}
          kind="plain"
          icon="arrow-left"
        >
          Back
        </ButtonLink>
      </div>
    </LoadingContainer>
  );
};

interface CardItemListProps {
  cardRecipients: CardMatchEventFragment[];
  removeCardRecipient: (id: string) => void;
}

const CardItemList: React.FC<CardItemListProps> = ({
  cardRecipients,
  removeCardRecipient,
}) => {
  return pipe(
    cardRecipients,
    O.fromPredicate(A.isNonEmpty),
    O.fold(
      () => null,
      (matchCardRecipients) => {
        return (
          <ul key="matchCardRecipients" className="space-y-2">
            {matchCardRecipients.map(({ event: cardType, id, player }) => {
              return (
                <li key={id} className="flex items-center">
                  <div className="flex items-center">
                    <CardIcon
                      color={cardType}
                      title={startCase(cardType)}
                      className="mr-2"
                    />
                    {player.name}
                  </div>
                  <button
                    onClick={() => removeCardRecipient(id)}
                    className="pointer ml-auto text-grey-500 hover:text-grey-800"
                    title={`Remove ${player.name}'s ${cardType} card`}
                  >
                    <FeatherIcon icon="x" />
                  </button>
                </li>
              );
            })}
          </ul>
        );
      },
    ),
  );
};
